import React, { useState, useEffect, useMemo, useRef } from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import throttle from 'lodash/throttle';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import { useTranslation } from "react-i18next";
import { MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { api } from 'common';
import { v4 as uuidv4 } from 'uuid';


const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  inputRtl: {
    "& label": {
      right: 75,
      left: "auto",
      fontFamily: FONT_FAMILY,
    },
  },
  textField: {
    "& label.Mui-focused": {
      color: MAIN_COLOR,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: MAIN_COLOR,
      },
    },
    "& input": {
      fontFamily: FONT_FAMILY,
    },
    "& .MuiAutocomplete-popper": {
      zIndex: 10000,
    },
  },
}));

export default function GoogleMapsAutoComplete(props) {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const inputRef = useRef(null);
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const {
    fetchPlacesAutocomplete
  } = api;
  const [UUID, setUUID] = useState();

  useEffect(()=>{
    const newUuid = uuidv4();
    setUUID(newUuid);
    return () => {
      setUUID(null);
    };
  },[]);

  // Keep the visible input in sync when parent provides a `value` object
  useEffect(() => {
    try {
      if (props && props.value) {
        const v = props.value;
        const desc = (typeof v === 'string') ? v : (v.description || '');
        // Only update when different to avoid fighting user's typing
        if (desc !== inputValue) setInputValue(desc);
      }
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props && props.value]);


  const search = useMemo(
    () => throttle(async (searchKeyword, callback) => {
      const safeCallback = typeof callback === 'function' ? callback : () => {};
  if (!searchKeyword) return safeCallback([]);

      const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.') || window.location.hostname === '0.0.0.0');

      // In dev (localhost) avoid proxy calls that trigger CORS preflight with Authorization header.
      if (!isLocalhost) {
        // First try server-backed Google Places via api.fetchPlacesAutocomplete
        try {
          const results = await fetchPlacesAutocomplete(searchKeyword, UUID);
          if (results && results.length > 0) return callback(results);
        } catch (err) {
          // fallback to Nominatim below
          console.warn('fetchPlacesAutocomplete failed, falling back to Nominatim', err);
        }
      }

      // Fallback / dev: use Nominatim (OpenStreetMap) — worldwide coverage, CORS-friendly.
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchKeyword)}&limit=6&addressdetails=1`;
        const resp = await window.fetch(url, { headers: { 'Accept-Language': 'fr,en' } });
        if (!resp || !resp.ok) return safeCallback([]);
        const json = await resp.json();
        const nominatimResults = (json || []).map(item => ({
          description: item.display_name,
          place_id: `nominatim_${item.place_id}`,
          id: item.place_id,
          lat: item.lat ? parseFloat(item.lat) : null,
          lon: item.lon ? parseFloat(item.lon) : null,
          source: 'nominatim'
        }));
        return safeCallback(nominatimResults);
      } catch (err2) {
        console.warn('Nominatim geocoder fallback failed', err2);
        return safeCallback([]);
      }
    }, 200),
    [fetchPlacesAutocomplete, UUID]
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions([]);
      return;
    }

    search(inputValue, (results) => {
      if (active) {
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, search]);

  return (
  <Autocomplete
      style={props.style}
      noOptionsText={null}
      // Plus strict: seulement ouvrir quand on a vraiment des options valides
      open={Boolean(options && options.length > 0 && inputValue && inputValue.trim().length > 2)}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      freeSolo={Boolean(props.freeSolo)}
      clearOnBlur={!props.freeSolo}
      inputValue={inputValue}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={props.value}
      // Props pour améliorer le z-index
      PopperComponent={(props) => (
        <div {...props} style={{ ...props.style, zIndex: 10000 }} />
      )}
      componentsProps={{
        popper: {
          style: { zIndex: 10000 }
        }
      }}
      // Fermer immédiatement quand on perd le focus
      onClose={(event, reason) => {
        if (reason === 'blur' || reason === 'escape') {
          setOptions([]);
        }
      }}
      isOptionEqualToValue={(option, value) => {
        // handle null/undefined
        if (option == null && value == null) return true;
        if (option == null || value == null) return false;
        // strings
        if (typeof option === 'string' || typeof value === 'string') return option === value;
        // prefer place_id, then id, then description
        if (option.place_id && value.place_id) return option.place_id === value.place_id;
        if (option.id && value.id) return option.id === value.id;
        return (option.description || '') === (value.description || '');
      }}
      onChange={(event, newValue) => {
        // Fermer les options immédiatement pour éviter les divs vides
        setOptions([]);
        
        // Normalize when Autocomplete returns a plain string (e.g., user typed+enter)
        if (typeof newValue === 'string') {
          const norm = { description: newValue };
          if (props && typeof props.onChange === 'function') props.onChange(norm);
          setInputValue(norm.description || '');
          // Blur l'input pour fermer complètement
          setTimeout(() => {
            if (inputRef.current && inputRef.current.blur) inputRef.current.blur();
          }, 50);
          return;
        }

        if (!newValue) { 
          if (props && typeof props.onChange === 'function') props.onChange(newValue); 
          return; 
        }

        // Normalize lat/lon to coords structure if present
        if (newValue.lat && newValue.lon && !newValue.coords) {
          newValue.coords = { lat: parseFloat(newValue.lat), lng: parseFloat(newValue.lon) };
        }

        // If result comes from Nominatim fallback (has lat/lon) use coords
        if (newValue.source === 'nominatim' || (newValue.lat && newValue.lon)) {
          newValue.coords = newValue.coords || { lat: parseFloat(newValue.lat), lng: parseFloat(newValue.lon) };
          if (props && typeof props.onChange === 'function') props.onChange(newValue);
          setInputValue(newValue.description || '');
          // Fermer complètement avec blur
          setTimeout(() => {
            if (inputRef.current && inputRef.current.blur) inputRef.current.blur();
          }, 50);
          return;
        }

        // Otherwise, try geocoding via place_id (Google)
        if (newValue && newValue.place_id) {
          setInputValue(newValue.description || '');
          // Fermer complètement avec blur
          setTimeout(() => {
            if (inputRef.current && inputRef.current.blur) inputRef.current.blur();
          }, 50);
          geocodeByPlaceId(newValue.place_id)
            .then(results => {
              if (results.length > 0) {
                newValue.coords = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
                newValue.placeDetails = results[0];
              }
              if (props && typeof props.onChange === 'function') props.onChange(newValue);
            })
            .catch(() => {
              // If geocode fails, still return the selection with no coords
              if (props && typeof props.onChange === 'function') props.onChange(newValue);
            });
          return;
        }

        // Fallback: pass selection as-is
        if (props && typeof props.onChange === 'function') props.onChange(newValue);
    setOptions([]);
      }}
          onInputChange={(event, newInputValue) => {
      setInputValue(newInputValue);
      // expose input changes to parent if requested (useful for quick-book raw text)
      if (props && typeof props.onInputChange === 'function') props.onInputChange(newInputValue);
          }}
    renderInput={(params) => {
        // allow parent to inject a startAdornment (icon) and to force input text color
        // Put the placeholder as the native input placeholder (not the floating label)
        // so it remains visible on dark backgrounds. Preserve any existing inputProps.
  const inputProps = Object.assign({}, params.inputProps || {}, { style: { ...(params.inputProps && params.inputProps.style), color: props.inputTextColor || undefined }, placeholder: props.placeholder || (params.inputProps && params.inputProps.placeholder) });
        const InputProps = Object.assign({}, params.InputProps || {});
        if (props.startAdornment) InputProps.startAdornment = props.startAdornment;
        return (
          <TextField
            {...params}
            // do not use the floating label; show placeholder inside the input instead
            label={undefined}
            variant={props.variant}
            className={[isRTL ? classes.inputRtl : '', classes.textField].join(" ")}
            fullWidth
            InputProps={InputProps}
            inputProps={inputProps}
            inputRef={inputRef}
            onBlur={() => { 
              // Petit délai pour permettre la sélection avant de fermer
              setTimeout(() => setOptions([]), 150); 
            }}
          />
        );
      }}
      renderOption={(props, option) => {
        // avoid spreading a `key` inside props (React warns if key is spread)
        const { key, ...rest } = props || {};
        return (
          <li key={key} {...rest}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY }}>
                  {option.description || "No description"}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}